'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastVariant = 'default' | 'success' | 'error' | 'info';

export type ToastItem = {
  id: string;
  title?: string;
  description?: string;
  variant: ToastVariant;
  createdAt: number;
  durationMs: number;
};

type ToastContextValue = {
  toasts: ToastItem[];
  show: (toast: Omit<ToastItem, 'id' | 'createdAt'>) => void;
  dismiss: (id: string) => void;
  clear: () => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timersRef.current.get(id);
    if (handle) {
      window.clearTimeout(handle);
      timersRef.current.delete(id);
    }
  }, []);

  const clear = useCallback(() => {
    setToasts([]);
    for (const handle of timersRef.current.values()) window.clearTimeout(handle);
    timersRef.current.clear();
  }, []);

  const show = useCallback(
    (toast: Omit<ToastItem, 'id' | 'createdAt'>) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const item: ToastItem = { ...toast, id, createdAt: Date.now() };
      setToasts((prev) => [item, ...prev].slice(0, 5));

      const duration = Math.max(1200, Math.min(item.durationMs || 2500, 10000));
      const handle = window.setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, handle);
    },
    [dismiss],
  );

  const success = useCallback(
    (title: string, description?: string) =>
      show({ title, description, variant: 'success', durationMs: 2200 }),
    [show],
  );
  const error = useCallback(
    (title: string, description?: string) =>
      show({ title, description, variant: 'error', durationMs: 3500 }),
    [show],
  );
  const info = useCallback(
    (title: string, description?: string) =>
      show({ title, description, variant: 'info', durationMs: 2600 }),
    [show],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, show, dismiss, clear, success, error, info }),
    [toasts, show, dismiss, clear, success, error, info],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

